import classNames from 'classnames';
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useResizeObserver from '@alltrails/core/hooks/useResizeObserver';
import styles from './Sidebar.module.scss';
import { SidebarProvider } from './SidebarContext';

type SidebarProps = {
  children?: ReactNode;
  collapsed?: boolean;
  id: string;
  onCollapseRequest?: () => void;
};

/**
 * A component wrapper that supports collapsibility, to be used in conjunction with the `useSidebar` hook.
 * This is split from the hook to allow for flexibility in what consumers can render in either the header or content elements with the [parallel routing paradigm](https://nextjs.org/docs/app/building-your-application/routing/parallel-routes) provided by NextJS.
 *
 * For an example, see the `Sidebar.stories.tsx` story in the storybook package.
 *
 * @param children - All sidebar content
 * @param collapsed - Whether the sidebar is collapsed
 * @param id - A unique identifier for the sidebar
 * @param onCollapseRequest - A callback function to update the collapsed state
 */
const Sidebar = ({ children, collapsed = false, id, onCollapseRequest }: SidebarProps) => {
  const [collapseBehavior, setCollapseBehavior] = useState<'full' | 'vertical-only' | 'none'>('none');
  const [preventHeightRecalculation, setPreventHeightRecalculation] = useState(false);
  const [fullHeight, setFullHeight] = useState(false);

  const containerHeightRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const containerHeightResizeHandler = useCallback(entries => {
    const { height } = entries[0].target.getBoundingClientRect();
    setContainerHeight(height);
  }, []);
  const [observeContainerHeight, disconnectContainerHeight] = useResizeObserver(containerHeightResizeHandler);
  useEffect(() => {
    observeContainerHeight(containerHeightRef.current);
    return disconnectContainerHeight;
  }, [observeContainerHeight, disconnectContainerHeight]);

  const [titleHeight, setTitleHeight] = useState<number>(0);
  const [contentHeight, setContentHeight] = useState<number>(0);
  const [footerHeight, setFooterHeight] = useState<number>(0);

  const prevMaxContentHeight = useRef<number | undefined>(undefined);
  const maxContentHeight = useMemo(() => {
    // Allow preventing height recalculations once we have an initial value
    if (preventHeightRecalculation && Boolean(prevMaxContentHeight.current)) {
      return prevMaxContentHeight.current;
    }

    const newMaxContentHeight = containerHeight - titleHeight - footerHeight;
    prevMaxContentHeight.current = newMaxContentHeight;
    return newMaxContentHeight;
  }, [containerHeight, footerHeight, preventHeightRecalculation, titleHeight]);

  const prevHeight = useRef<number | undefined>(undefined);
  const height = useMemo(() => {
    // collapsed sidebar should only show the title
    if (collapsed) {
      return titleHeight;
    }

    // Allow preventing height recalculations once we have an initial value
    if (preventHeightRecalculation && Boolean(prevHeight.current)) {
      return prevHeight.current;
    }

    const newHeight = fullHeight ? containerHeight : Math.min(containerHeight, titleHeight + contentHeight + footerHeight);
    prevHeight.current = newHeight;
    return newHeight;
  }, [collapsed, containerHeight, contentHeight, footerHeight, fullHeight, preventHeightRecalculation, titleHeight]);

  return (
    <div
      className={classNames(styles.container, {
        [styles.collapsedWidth]: collapsed && collapseBehavior === 'full'
      })}
      data-sidebar-collapse-behavior={collapseBehavior}
    >
      {/* Just used to track the overall height available to the sidebar */}
      <div className={styles.containerHeight} ref={containerHeightRef} />
      <SidebarProvider
        collapsed={collapsed}
        maxContentHeight={maxContentHeight}
        setCollapseBehavior={setCollapseBehavior}
        setContentHeight={setContentHeight}
        setFooterHeight={setFooterHeight}
        setFullHeight={setFullHeight}
        setPreventHeightRecalculation={setPreventHeightRecalculation}
        setTitleHeight={setTitleHeight}
        toggleCollapse={onCollapseRequest}
      >
        <div className={styles.sidebar} id={id} style={{ height }}>
          {children}
        </div>
      </SidebarProvider>
    </div>
  );
};

export default Sidebar;
